import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import EmbedFrame from '../components/EmbedFrame';
import SafeHtml from '../components/SafeHtml';
import DiscussionBoard from '../components/DiscussionBoard';
import ModulePill from '../components/ModulePill';
import LearnExTab from '../components/LearnExTab';
import { getModule, api } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useTutorScope } from '../context/TutorScopeContext';

const TABS = [
  { key: 'summary', label: 'Overview' },
  { key: 'presentation', label: 'Presentation' },
  { key: 'video', label: 'Video' },
  { key: 'assignment', label: 'Assignment' },
  { key: 'lab', label: 'Lab' },
  { key: 'discussion', label: 'Discussion' },
  { key: 'references', label: 'References' },
  { key: 'learnex', label: 'LearnEx ★' },
  { key: 'quiz', label: 'Quiz' },
];

const EmptyState = ({ label }) => (
  <div className="content-frame">
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
           style={{ background: 'var(--unthsc-green-light)', color: 'var(--unthsc-green-dark)' }}>
        <span className="text-2xl font-bold">·</span>
      </div>
      <div className="text-gray-800 font-semibold mb-1">No {label} yet</div>
      <div className="text-sm text-gray-500">Content will appear here once added by the Developer.</div>
    </div>
  </div>
);

const isAvailable = (topic, key) => {
  if (!topic) return false;
  switch (key) {
    case 'summary': return !!(topic.summary && stripTags(topic.summary).trim());
    case 'presentation': return !!topic.presentation_url;
    case 'video': return !!topic.video_url;
    case 'assignment': return !!(stripTags(topic.assignment_text || '').trim() || topic.assignment_url);
    case 'lab': return !!(stripTags(topic.lab_text || '').trim() || topic.lab_url);
    case 'discussion': return true; // always available — native board lives here
    case 'references': return !!(stripTags(topic.references_text || '').trim() || topic.references_url);
    case 'learnex': return true; // always available — its own UI surfaces emptiness
    case 'quiz': return !!topic.quiz_url;
    default: return false;
  }
};

const stripTags = (s) => (s || '').replace(/<[^>]*>/g, '').trim();

const ModuleViewer = () => {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [module, setModule] = useState(null);
  const [error, setError] = useState('');

  const isStudentPreview = location.pathname.startsWith('/preview-as-student');
  const effectiveRole = isStudentPreview ? 'student' : user?.role;

  const topicId = searchParams.get('topic');
  // Students land on LearnEx by default; faculty / developers stay on
  // the Overview tab so they can author content.
  const defaultTab = (user?.role === 'student' && !location.pathname.startsWith('/preview-as-faculty'))
    ? 'learnex' : 'summary';
  const tab = searchParams.get('tab') || defaultTab;

  useEffect(() => {
    getModule(moduleId).then(setModule).catch((e) => {
      setError(e.response?.data?.detail || 'Failed to load module');
    });
  }, [moduleId]);

  // Pull bulk LearnEx progress for sidebar chips. Re-fetches whenever the
  // active tab changes (so ticking an item updates the chip immediately).
  useEffect(() => {
    let cancel = false;
    api.get(`/api/modules/${moduleId}/learnex/progress`)
      .then((r) => {
        if (cancel) return;
        const map = {};
        (r.data?.topics || []).forEach((t) => { map[t.topic_id] = t; });
        setLearnexProgress(map);
      })
      .catch(() => { /* non-fatal — chips just won't render */ });
    return () => { cancel = true; };
  }, [moduleId, tab, topicId]);

  const currentTopic = useMemo(() => {
    if (!module) return null;
    if (!topicId) return null;
    return (module.topics || []).find((t) => t.id === topicId) || null;
  }, [module, topicId]);

  // Push module/topic scope up to the app-level AI tutor so the
  // single <AITutorLauncher /> knows what the user is looking at.
  const tutor = useTutorScope();
  useEffect(() => {
    if (!module) return;
    tutor.setScope({
      module: { id: module.id, code: `M${module.order ?? '?'}`, name: module.title },
      topic: currentTopic ? { id: currentTopic.id, name: currentTopic.title, title: currentTopic.title } : null,
    });
  }, [module, currentTopic, tutor]);

  const setTab = (k) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', k);
    setSearchParams(next, { replace: true });
  };

  const selectTopic = (tid) => {
    const next = new URLSearchParams(searchParams);
    next.set('topic', tid);
    // Students land directly on LearnEx (the topic-level entry point);
    // faculty and developers land on Overview so they can curate.
    const landing = effectiveRole === 'student' ? 'learnex' : 'summary';
    next.set('tab', landing);
    setSearchParams(next, { replace: true });
  };

  // Jump directly to a specific tab on a specific topic — used by the
  // sidebar "🎯 LearnEx" pill so students can land one click in.
  const selectTopicTab = (tid, tabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('topic', tid);
    next.set('tab', tabKey);
    setSearchParams(next, { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-4xl mx-auto px-6 py-10">
          <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">{error}</div>
          <button onClick={() => navigate(-1)} className="btn-outline mt-4">Go back</button>
        </main>
      </div>
    );
  }
  if (!module) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-4xl mx-auto px-6 py-10 text-gray-500">Loading…</main>
      </div>
    );
  }

  const backPath = isStudentPreview ? '/preview-as-student' : (user ? `/${user.role}` : '/login');
  const isStudentDraft = effectiveRole === 'student' && module.status !== 'published';
  if (isStudentDraft) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{module.title}</h1>
          <p className="text-gray-500 mb-6">This module is not yet available. Please check back soon.</p>
          <Link to={backPath} className="btn-primary inline-block">Back to dashboard</Link>
        </main>
      </div>
    );
  }

  const topics = module.topics || [];

  return (
    <div className="min-h-screen" data-testid="module-viewer">
      <Navbar />
      {isStudentPreview && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Preview as Student</span>
              <span className="ml-2 opacity-80">— same view your enrolled students get.</span>
            </div>
            <Link to="/developer" className="text-xs font-semibold underline text-amber-900"
                  data-testid="exit-preview-link-viewer">
              Exit preview ↩
            </Link>
          </div>
        </div>
      )}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to={backPath} className="text-sm font-semibold hover:underline"
                  style={{ color: 'var(--unthsc-blue)' }} data-testid="back-to-dashboard-link">
              ← Back to dashboard
            </Link>
            <div className="mt-2 mb-1">
              <ModulePill number={module.order} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 leading-tight" data-testid="module-viewer-title">
              {module.title}
            </h1>
          </div>
          <span className={module.status === 'published' ? 'badge-published' : 'badge-draft'}>
            {module.status === 'published' ? 'Published' : 'Draft'}
          </span>
        </div>

        {/* Two-column layout: topic sidebar + content */}
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          {/* Topic sidebar */}
          <aside className="card p-3" data-testid="topic-sidebar">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 px-2 pt-1 pb-2">
              Topics
            </div>
            {topics.length === 0 ? (
              <div className="text-sm text-gray-500 italic px-2 py-3">No topics yet.</div>
            ) : (
              <ul className="space-y-1">
                {topics.map((t) => {
                  const active = t.id === currentTopic?.id;
                  return (
                    <li key={t.id}>
                      <button onClick={() => selectTopic(t.id)}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                active ? 'text-white font-semibold' : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              style={active ? { background: 'var(--unthsc-green)' } : undefined}
                              data-testid={`topic-pick-${t.order}`}>
                        <div className="text-[10px] uppercase opacity-80 tracking-wider">Topic {t.order}</div>
                        <div className="truncate">{t.title || 'Untitled'}</div>
                      </button>

                      {/* Sub-items: show only when this topic is active */}
                      {active && (
                        <ul className="mt-1 ml-3 border-l border-gray-200 pl-2 space-y-0.5">
                          {TABS.map((tb) => {
                            const available = isAvailable(t, tb.key);
                            if (!available) return null;
                            const isCurrent = tab === tb.key;
                            return (
                              <li key={tb.key}>
                                <button onClick={() => setTab(tb.key)}
                                        className={`w-full text-left text-xs px-2 py-1 rounded transition-colors ${
                                          isCurrent
                                            ? 'font-semibold'
                                            : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                        style={isCurrent ? { color: 'var(--unthsc-green-dark)' } : undefined}
                                        data-testid={`sidebar-tab-${tb.key}`}>
                                  · {tb.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Content area */}
          <section>
            {!currentTopic ? (
              <div className="card p-10 text-center text-gray-500" data-testid="no-topic-selected">
                {topics.length === 0
                  ? "This module has no topics yet."
                  : "Select a topic from the left to begin."}
              </div>
            ) : (
              <>
                {/* Tab header — current section name (sidebar is the navigator) */}
                <div className="mb-4 flex items-baseline gap-3 border-b border-gray-200 pb-2"
                     data-testid="topic-section-header">
                  <h2 className="text-lg font-bold" style={{ color: 'var(--gr-navy, #1A2B4C)' }}>
                    {(TABS.find((tb) => tb.key === tab) || {}).label || ''}
                  </h2>
                  <span className="text-xs text-gray-500">
                    Topic {currentTopic.order} · {currentTopic.title}
                  </span>
                </div>

                {/* Tab content */}
                <div className="min-h-[420px]">
                  {tab === 'summary' && (
                    isAvailable(currentTopic, 'summary') ? (
                      <div className="content-frame" data-testid="summary-content">
                        <SafeHtml html={currentTopic.summary} />
                      </div>
                    ) : <EmptyState label="summary" />
                  )}

                  {tab === 'presentation' && (
                    isAvailable(currentTopic, 'presentation') ? (
                      <div data-testid="presentation-content">
                        <EmbedFrame kind="pdf" src={currentTopic.presentation_url} title="Presentation" aspect="tall" />
                      </div>
                    ) : <EmptyState label="presentation" />
                  )}

                  {tab === 'video' && (
                    isAvailable(currentTopic, 'video') ? (
                      <div data-testid="video-content">
                        <EmbedFrame kind="video" src={currentTopic.video_url} title="Video" aspect="video" />
                      </div>
                    ) : <EmptyState label="video" />
                  )}

                  {tab === 'assignment' && (
                    isAvailable(currentTopic, 'assignment') ? (
                      <div data-testid="assignment-content" className="space-y-4">
                        {currentTopic.assignment_text && (
                          <div className="content-frame"><SafeHtml html={currentTopic.assignment_text} /></div>
                        )}
                        {currentTopic.assignment_url && (
                          /\.(docx?|pptx?|xlsx?|pdf)(\?|$)/i.test(currentTopic.assignment_url) ? (
                            <EmbedFrame kind="pdf" src={currentTopic.assignment_url}
                                        title="Assignment document" aspect="tall" />
                          ) : (
                            <a href={currentTopic.assignment_url} target="_blank" rel="noreferrer"
                               className="btn-secondary inline-block">
                              Open assignment file ↗
                            </a>
                          )
                        )}
                      </div>
                    ) : <EmptyState label="assignment" />
                  )}

                  {tab === 'lab' && (
                    isAvailable(currentTopic, 'lab') ? (
                      <div data-testid="lab-content" className="space-y-4">
                        {currentTopic.lab_text && (
                          <div className="content-frame"><SafeHtml html={currentTopic.lab_text} /></div>
                        )}
                        {currentTopic.lab_url && (
                          /\.(docx?|pptx?|xlsx?|pdf)(\?|$)/i.test(currentTopic.lab_url) ? (
                            <EmbedFrame kind="pdf" src={currentTopic.lab_url} title="Lab document" aspect="tall" />
                          ) : (
                            <a href={currentTopic.lab_url} target="_blank" rel="noreferrer"
                               className="btn-secondary inline-block">Open lab resource ↗</a>
                          )
                        )}
                      </div>
                    ) : <EmptyState label="lab" />
                  )}

                  {tab === 'discussion' && (
                    <div data-testid="discussion-content" className="space-y-4">
                      {currentTopic.discussion_text && (
                        <div className="content-frame"><SafeHtml html={currentTopic.discussion_text} /></div>
                      )}
                      {currentTopic.discussion_url && (
                        <a href={currentTopic.discussion_url} target="_blank" rel="noreferrer"
                           className="btn-outline inline-block text-sm">
                          Open external discussion forum ↗
                        </a>
                      )}
                      <DiscussionBoard moduleId={module.id} topicId={currentTopic.id} />
                    </div>
                  )}

                  {tab === 'references' && (
                    isAvailable(currentTopic, 'references') ? (
                      <div data-testid="references-content" className="space-y-4">
                        {currentTopic.references_text && (
                          <div className="content-frame"><SafeHtml html={currentTopic.references_text} /></div>
                        )}
                        {currentTopic.references_url && (
                          /\.(docx?|pptx?|xlsx?|pdf)(\?|$)/i.test(currentTopic.references_url) ? (
                            <EmbedFrame kind="pdf" src={currentTopic.references_url}
                                        title="References" aspect="tall" />
                          ) : (
                            <a href={currentTopic.references_url} target="_blank" rel="noreferrer"
                               className="btn-secondary inline-block">Open references ↗</a>
                          )
                        )}
                      </div>
                    ) : <EmptyState label="references" />
                  )}

                  {tab === 'learnex' && (
                    <LearnExTab
                      moduleId={module.id}
                      topicId={currentTopic.id}
                      topicTitle={currentTopic.title}
                    />
                  )}

                  {tab === 'quiz' && (
                    isAvailable(currentTopic, 'quiz') ? (
                      <div data-testid="quiz-content">
                        <EmbedFrame kind="quiz" src={currentTopic.quiz_url} title="Quiz" aspect="tall" />
                      </div>
                    ) : <EmptyState label="quiz" />
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      {/* AI Tutor is provided by the app-level <AITutorLauncher />.
          The `useTutorScope().setScope(...)` effect above tells the
          launcher which module/topic is currently active. */}
    </div>
  );
};

export default ModuleViewer;
